
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UniversitySearchBox from './UniversitySearchBox';

interface EnhancedSignUpFormProps {
  role: 'student' | 'landlord';
  onBack: () => void;
  onComplete?: () => void;
}

const EnhancedSignUpForm = ({ role, onBack, onComplete }: EnhancedSignUpFormProps) => {
  const { signUp } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    gender: '',
    universityId: ''
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Full name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = t('full_name_required', 'الاسم الكامل مطلوب', 'Full name is required');
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t('full_name_too_short', 'الاسم الكامل يجب أن يكون حرفين على الأقل', 'Full name must be at least 2 characters');
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = t('email_required', 'البريد الإلكتروني مطلوب', 'Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('invalid_email_format', 'تنسيق البريد الإلكتروني غير صحيح', 'Please enter a valid email address');
      }
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = t('gender_required', 'الجنس مطلوب', 'Gender is required');
    }

    // University validation for students
    if (role === 'student' && !formData.universityId) {
      newErrors.universityId = t('university_required', 'الجامعة مطلوبة', 'University is required');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('password_required', 'كلمة المرور مطلوبة', 'Password is required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('password_min_length', 'كلمة المرور يجب أن تكون 8 أحرف على الأقل', 'Password must be at least 8 characters');
    } else {
      // Check password strength
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumbers = /\d/.test(formData.password);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
        newErrors.password = t('password_strength', 'كلمة المرور يجب أن تحتوي على أحرف كبيرة وصغيرة وأرقام', 'Password must contain uppercase, lowercase letters and numbers');
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('confirm_password_required', 'تأكيد كلمة المرور مطلوب', 'Password confirmation is required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwords_dont_match', 'كلمات المرور غير متطابقة', 'Passwords do not match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('form_validation_error', 'يرجى تصحيح الأخطاء في النموذج', 'Please fix the errors in the form'));
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await signUp(formData.email, formData.password, {
        full_name: formData.fullName,
        role: role,
        gender: formData.gender,
        university_id: role === 'student' ? formData.universityId : null
      });

      if (error) {
        console.error('Signup error:', error);
        toast.error(`${t('signup_failed', 'فشل في إنشاء الحساب', 'Signup failed')}: ${error.message}`);
        return;
      }

      // Show success notification and set up success screen
      console.log('Signup successful, confirmation email sent');
      setUserEmail(formData.email);
      setSignUpSuccess(true);
      toast.success(t('check_email_verify', 'تحقق من بريدك الإلكتروني للتحقق من حسابك', 'Check your email to verify your account'));
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast.error(error?.message || t('signup_failed', 'فشل في إنشاء الحساب', 'Failed to create account'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleGoToSignIn = () => {
    navigate('/auth');
  };

  // Show success screen after signup
  if (signUpSuccess) {
    return (
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <CheckCircle className="h-16 w-16 text-green-500" />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('confirmation_email_sent', 'تم إرسال بريد التأكيد', 'Confirmation Email Sent!')}
          </h2>
          <p className="text-gray-600 mb-4">
            {t('check_email_confirmation', 'تحقق من بريدك الإلكتروني واضغط على رابط التأكيد لتفعيل حسابك', 'Check your email and click the confirmation link to activate your account')}
          </p>
          <p className="text-sm text-gray-500">
            <strong>{userEmail}</strong>
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border">
          <div className="flex items-center gap-2 text-blue-800 mb-2">
            <Mail className="h-4 w-4" />
            <span className="font-medium">
              {t('next_steps', 'الخطوات التالية', 'Next Steps')}
            </span>
          </div>
          <ol className="text-sm text-blue-700 text-left space-y-1">
            <li>1. {t('check_email', 'تحقق من بريدك الإلكتروني', 'Check your email inbox')}</li>
            <li>2. {t('click_confirmation_link', 'اضغط على رابط التأكيد', 'Click the confirmation link')}</li>
            <li>3. {t('return_to_sign_in', 'ارجع لتسجيل الدخول بكلمة المرور', 'Return here to sign in with your password')}</li>
          </ol>
        </div>

        <div className="space-y-3">
          <Button onClick={handleGoToSignIn} className="w-full">
            {t('go_to_sign_in', 'الانتقال لتسجيل الدخول', 'Go to Sign In')}
          </Button>
          
          <Button variant="outline" onClick={onBack} className="w-full">
            {t('create_another_account', 'إنشاء حساب آخر', 'Create Another Account')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Button
        type="button"
        variant="ghost"
        onClick={onBack}
        className="mb-4 p-0 h-auto"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('back', 'رجوع', 'Back')}
      </Button>

      <div className="space-y-4">
        <div>
          <Label htmlFor="fullName">
            {t('full_name', 'الاسم الكامل', 'Full Name')}
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="fullName"
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange('fullName', e.target.value)}
            className={errors.fullName ? 'border-red-300' : ''}
            required
          />
          {errors.fullName && (
            <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.fullName}</span>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="email">
            {t('email', 'البريد الإلكتروني', 'Email')}
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={errors.email ? 'border-red-300' : ''}
            required
          />
          {errors.email && (
            <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.email}</span>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="gender">
            {t('gender', 'الجنس', 'Gender')}
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
            <SelectTrigger className={errors.gender ? 'border-red-300' : ''}>
              <SelectValue placeholder={t('select_gender', 'اختر الجنس', 'Select Gender')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t('male', 'ذكر', 'Male')}</SelectItem>
              <SelectItem value="female">{t('female', 'أنثى', 'Female')}</SelectItem>
            </SelectContent>
          </Select>
          {errors.gender && (
            <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.gender}</span>
            </div>
          )}
        </div>

        {role === 'student' && (
          <div>
            <Label>
              {t('university', 'الجامعة', 'University')}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <UniversitySearchBox
              value={formData.universityId}
              onValueChange={(value) => handleInputChange('universityId', value)}
            />
            {errors.universityId && (
              <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                <AlertCircle className="h-3 w-3" />
                <span>{errors.universityId}</span>
              </div>
            )}
          </div>
        )}

        <div>
          <Label htmlFor="password">
            {t('password', 'كلمة المرور', 'Password')}
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={errors.password ? 'border-red-300' : ''}
              required
              minLength={8}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {errors.password && (
            <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.password}</span>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="confirmPassword">
            {t('confirm_password', 'تأكيد كلمة المرور', 'Confirm Password')}
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={errors.confirmPassword ? 'border-red-300' : ''}
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {errors.confirmPassword && (
            <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.confirmPassword}</span>
            </div>
          )}
        </div>
      </div>

      {/* Email Verification Notice */}
      <div className="bg-blue-50 p-4 rounded-lg border">
        <div className="flex items-center gap-2 text-blue-800 mb-2">
          <Mail className="h-4 w-4" />
          <span className="font-medium">
            {t('email_confirmation', 'تأكيد البريد الإلكتروني', 'Email Confirmation')}
          </span>
        </div>
        <p className="text-sm text-blue-700">
          {t('confirmation_email_desc', 'سيتم إرسال بريد تأكيد إلى عنوانك الإلكتروني. يجب تأكيد البريد قبل تسجيل الدخول.', 'A confirmation email will be sent to your email address. You must confirm your email before signing in.')}
        </p>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? t('creating_account', 'إنشاء الحساب...', 'Creating Account...') : t('create_account', 'إنشاء حساب', 'Create Account')}
      </Button>
    </form>
  );
};

export default EnhancedSignUpForm;
